
import React from 'react';
import { Device } from '@/components/device-health/gis-map/types';
import { applyFilters, createPlaceholderAlert } from './utils/alertFilters';
import EmptyAlertsState from './components/EmptyAlertsState';
import AlertsGroupSection from './components/AlertsGroupSection';
import { MaintenanceAlertData } from './types/maintenanceTypes';

interface MaintenanceAlertsListProps {
  alerts: MaintenanceAlertData[];
  selectedDeviceId?: string;
  deviceData?: Device | null;
  assetTypeFilter: string | null;
  priorityFilter: string | null;
  facilityFilter: string | null;
}

const MaintenanceAlertsList: React.FC<MaintenanceAlertsListProps> = ({ 
  alerts, 
  selectedDeviceId,
  deviceData,
  assetTypeFilter,
  priorityFilter,
  facilityFilter
}) => {
  // Apply filters to the alerts
  const filteredAlerts = applyFilters(alerts, assetTypeFilter, priorityFilter, facilityFilter);

  // Group alerts by priority
  const criticalAlerts = filteredAlerts.filter(alert => alert.status === 'Alert');
  const mediumAlerts = filteredAlerts.filter(alert => alert.status === 'Warning');
  const lowAlerts = filteredAlerts.filter(alert => alert.status === 'Good');

  // If we have device data but no alerts match the device ID, create a placeholder alert
  const getAlertsToDisplay = () => {
    if (selectedDeviceId && deviceData && filteredAlerts.length === 0) {
      // Create a placeholder alert for the selected device
      const placeholderAlert = createPlaceholderAlert(deviceData);

      if (deviceData.status === 'Healthy') return { critical: [], medium: [], low: [placeholderAlert] };
      if (deviceData.status === 'Warning') return { critical: [], medium: [placeholderAlert], low: [] };
      return { critical: [placeholderAlert], medium: [], low: [] };
    }
    
    return { critical: criticalAlerts, medium: mediumAlerts, low: lowAlerts };
  };

  const { critical, medium, low } = getAlertsToDisplay();

  // Show empty state if no alerts match the filters
  if (filteredAlerts.length === 0 && (!selectedDeviceId || !deviceData)) {
    return <EmptyAlertsState />;
  }

  return (
    <div className="space-y-6">
      <AlertsGroupSection 
        title="Critical Alerts" 
        alerts={critical} 
        showSeparator={medium.length > 0 || low.length > 0} 
      />
      
      <AlertsGroupSection 
        title="Medium Alerts" 
        alerts={medium} 
        showSeparator={low.length > 0} 
      />
      
      <AlertsGroupSection 
        title="Low Alerts" 
        alerts={low} 
        showSeparator={false} 
      />
    </div>
  );
};

export default MaintenanceAlertsList;
